import React, {
  useMemo,
} from 'react';
import isNil from 'lodash/isNil';
import Popover from '@mui/material/Popover';
import {
  FormProvider,
  useForm,
} from 'react-hook-form';
import DisabledFormTooltip from 'core/components/DisabledFormTooltip';
import Controller from 'core/components/FormController';
import {
  ServiceFacilityDTO,
  UserDTO,
} from 'dtos';
import Button from 'pages/Dashboard/components/Button';
import ServiceFacilitySelect from 'pages/Dashboard/components/ServiceFacilitySelect';
import ProviderLookup from 'views/EMR/components/ProviderLookup';
import Wrapper from 'pages/Dashboard/components/Input/Wrapper';
import useUserInfo from 'pages/Dashboard/hooks/useUserInfo';
import {
  normalizeUser,
} from 'pages/Dashboard/utils/helper';
import {
  useFormAutoFocus,
} from 'views/EMR/hooks/common';

export type DoctorForm = {
  doctor?: UserDTO | null;
  serviceFacility?: ServiceFacilityDTO | null;
};

type Props = {
  anchorElement: HTMLElement;
  value?: DoctorForm;
  onConfirm: (value: DoctorForm) => void;
  onClose: () => void;
};

export default function DoctorSelectPopover({
  onConfirm,
  onClose,
  anchorElement,
  value,
}: Props) {
  const userInfo = useUserInfo();
  const currentDoctor = useMemo(
    () => {
      const { user } = userInfo ?? {};
      return user?.designation === 'Doctor' ? normalizeUser(user ?? {}, user?.userId) : null;
    },
    [userInfo],
  );

  const form = useForm<DoctorForm>({
    defaultValues: {
      ...value,
      doctor: value?.doctor ?? currentDoctor,
    },
    mode: 'all',
  });
  const {
    reset,
    control,
    handleSubmit,
    formState: { isValid },
  } = form;
  const isOpened = !isNil(anchorElement);

  const onSubmit = (data: DoctorForm) => {
    onConfirm(data);
    onClose();
  };

  const onCancel = () => {
    reset();
    onClose();
  };

  const doctorSelectRef = useFormAutoFocus({ enabled: true, delay: 200 });

  return (
    <Popover
      open={isOpened}
      onClose={onCancel}
      classes={{ paper: '!w-[608px] !overflow-visible' }}
      anchorEl={anchorElement}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
    >
      <FormProvider {...form}>
        <div className="p-4 flex flex-col w-full gap-form-row populate-form">
          <div className="flex w-full gap-form-field">
            <Controller
              name="doctor"
              control={control}
              rules={{ required: 'Add doctor for new appointment' }}
              render={({ field, fieldState: { invalid } }) => (
                <Wrapper
                  id="doctor"
                  label="Doctor"
                  className="flex-1 text-gray-500 text-xs max-w-[50%]"
                  error={invalid}
                  showErrorText={false}
                  required
                >
                  <ProviderLookup
                    {...field}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    inputRef={doctorSelectRef}
                  />
                </Wrapper>
              )}
            />
            <Controller
              name="serviceFacility"
              control={control}
              rules={{ required: 'Choose facility for the new appointment' }}
              render={({ field, fieldState: { invalid } }) => (
                <ServiceFacilitySelect
                  value={field?.value as ServiceFacilityDTO}
                  onChange={field.onChange}
                  label="Facility"
                  className="flex-1 text-xs max-w-[50%]"
                  error={invalid}
                  showErrorText={false}
                  required
                />
              )}
            />
          </div>
          <div className="flex gap-4 items-center">
            <DisabledFormTooltip>
              <Button
                onClick={handleSubmit(onSubmit)}
                color="primary"
                variant="contained"
                disabled={!isValid}
                className="nw-update-datetime-button"
              >
                Confirm
              </Button>
            </DisabledFormTooltip>
            <Button
              variant="outlined"
              onClick={onCancel}
            >
              Close
            </Button>
          </div>
        </div>
      </FormProvider>
    </Popover>
  );
}
